import { memo, useCallback, type CSSProperties } from 'react';
import { Card } from '@mantine/core';
import { IconCalendar } from '@tabler/icons-react';
import { useLocalizedDate } from '@/hooks/useLocalizedDate';
import { NoteVisibilityBadge } from '@/components/notes/NoteVisibilityBadge';
import { UserAvatar } from '@/components/user/UserAvatar';
import { ICON_SIZE_TAB } from '@/constants/ui';
import type { NoteListItem } from '@/types/notes';
import styles from './NoteCard.module.css';

interface NoteCardProps {
  note: NoteListItem;
  onClick: (noteId: string) => void;
  isSelected?: boolean;
}

export const NoteCard = memo(function NoteCard({
  note,
  onClick,
  isSelected = false,
}: NoteCardProps) {
  const { formatDateCompact, formatTime } = useLocalizedDate();

  const handleClick = useCallback(() => {
    onClick(note.shortId);
  }, [onClick, note.shortId]);

  const cardClassName = isSelected
    ? `${styles.card} ${styles.selected}`
    : styles.card;

  return (
    <Card
      className={cardClassName}
      onClick={handleClick}
      padding="sm"
      withBorder
    >
      <div className={styles.titleRow}>
        <div className={styles.titleGroup}>
          <div
            className={styles.colorDot}
            style={{ '--note-dot-color': note.color } as CSSProperties}
          />
          <span className={styles.title}>{note.title}</span>
        </div>
        <NoteVisibilityBadge
          visibility={note.visibility}
          className={styles.visibilityBadge}
        />
      </div>

      <div className={styles.authorRow}>
        <div className={styles.authorGroup}>
          <UserAvatar
            displayName={note.creatorName}
            avatarUrl={note.creatorAvatarUrl}
            size="xs"
          />
          <span className={styles.authorName}>{note.creatorName}</span>
        </div>
        <div className={styles.metaGroup}>
          <IconCalendar size={ICON_SIZE_TAB} className={styles.metaIcon} />
          <span className={styles.metaText}>
            {formatDateCompact(note.updatedAt ?? note.createdAt)}{' '}
            {formatTime(note.updatedAt ?? note.createdAt)}
          </span>
        </div>
      </div>
    </Card>
  );
});
